import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Package,
  Boxes,
  Tag,
  BookOpen,
  Mail,
  Terminal,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  Bell,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TILES = [
  {
    id: 'orders',
    label: 'Manage Orders',
    description: 'View, fulfill, and update orders. Track shipments and handle cancellations.',
    icon: Package,
    href: '/admin/orders',
  },
  {
    id: 'stock',
    label: 'Manage Stock',
    description: 'Adjust inventory levels, visibility, and pricing across the catalog.',
    icon: Boxes,
    href: '/admin/stock',
  },
  {
    id: 'promo',
    label: 'Promo Codes',
    description: 'Create discount codes, monitor redemption, and view conversion reports.',
    icon: Tag,
    href: '/admin/promo-codes',
  },
  {
    id: 'references',
    label: 'Reference Library',
    description: 'Curate scientific references and citations displayed on product detail pages.',
    icon: BookOpen,
    href: '/admin/references',
  },
  {
    id: 'inbox',
    label: 'Inbox',
    description: 'Customer contact-form submissions and support inquiries.',
    icon: Mail,
    href: '/admin/inbox',
  },
  {
    id: 'tools',
    label: 'Admin Terminal',
    description: 'Trigger TagadaPay catalog sync, order reconciliation, and custom admin requests.',
    icon: Terminal,
    href: '/admin/tools',
  },
];

const SEVERITY_STYLES = {
  danger: {
    badge: 'bg-red-600 hover:bg-red-600 text-white',
    border: 'border-red-200',
    accent: 'text-red-700',
    bg: 'bg-red-50/40',
    icon: AlertTriangle,
  },
  warn: {
    badge: 'bg-amber-500 hover:bg-amber-500 text-white',
    border: 'border-amber-200',
    accent: 'text-amber-700',
    bg: 'bg-amber-50/40',
    icon: AlertTriangle,
  },
  info: {
    badge: 'bg-sky-600 hover:bg-sky-600 text-white',
    border: 'border-sky-200',
    accent: 'text-sky-700',
    bg: 'bg-sky-50/40',
    icon: Bell,
  },
  ok: {
    badge: 'bg-slate-200 hover:bg-slate-200 text-slate-600',
    border: 'border-slate-200',
    accent: 'text-slate-500',
    bg: 'bg-slate-50/40',
    icon: CheckCircle,
  },
};

function NotificationCard({ item, onClick }) {
  const style = SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.ok;
  const Icon = style.icon;
  const isOk = item.severity === 'ok' || item.count === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-md border ${style.border} ${style.bg} p-4 transition-all hover:shadow-sm hover:-translate-y-0.5 group ${isOk ? 'opacity-70' : ''}`}
      data-testid={`notif-card-${item.key}`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-md flex items-center justify-center bg-white border ${style.border}`}>
          <Icon className={`h-4 w-4 ${style.accent}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <span className={`text-2xl font-heading font-medium ${isOk ? 'text-slate-500' : 'text-slate-900'}`} data-testid={`notif-count-${item.key}`}>
              {item.count}
            </span>
            <ChevronRight className={`h-4 w-4 ${style.accent} opacity-0 group-hover:opacity-100 transition-opacity`} />
          </div>
          <p className="text-sm font-medium text-slate-900 leading-tight">{item.label}</p>
          {item.hint && (
            <p className="text-xs text-slate-500 mt-1 leading-snug font-body">{item.hint}</p>
          )}
        </div>
      </div>
    </button>
  );
}

function TileCard({ tile, onClick }) {
  const Icon = tile.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-white rounded-md border border-slate-200 p-5 hover:shadow-md hover:border-[hsl(210,45%,40%)]/40 hover:-translate-y-0.5 transition-all group"
      data-testid={`admin-tile-${tile.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-md bg-[hsl(210,25%,92%)] group-hover:bg-[hsl(210,25%,88%)] flex items-center justify-center flex-shrink-0 transition-colors">
          <Icon className="h-5 w-5 text-[hsl(210,45%,35%)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-heading text-slate-900">{tile.label}</h3>
            <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-[hsl(210,45%,40%)] group-hover:translate-x-0.5 transition-all" />
          </div>
          <p className="text-xs text-slate-600 font-body leading-relaxed">{tile.description}</p>
        </div>
      </div>
    </button>
  );
}

export default function AdminDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNotifs = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/dashboard/notifications`, { withCredentials: true });
      setNotifs(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60_000); // refresh every minute
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  if (authLoading || !user || user.role !== 'admin') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-muted-foreground animate-spin" />
      </div>
    );
  }

  const actionable = notifs?.total_actionable || 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(210,45%,40%)] font-semibold font-body mb-1">Admin</p>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-light text-slate-900 font-heading">Dashboard</h1>
          <p className="mt-2 text-sm text-slate-600 font-body">
            Welcome back, {user.name || 'Admin'}.
            {actionable > 0 ? (
              <span className="ml-1">
                You have <span className="font-medium text-amber-700">{actionable}</span> item{actionable === 1 ? '' : 's'} needing attention.
              </span>
            ) : (
              <span className="ml-1 text-emerald-700">All clear — nothing urgent.</span>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setLoading(true); fetchNotifs(); }}
          disabled={loading}
          data-testid="admin-dashboard-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Notifications */}
      <section className="mb-10" data-testid="admin-notifications-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 font-body">Notifications</h2>
          {actionable > 0 && (
            <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-[10px]" data-testid="admin-actionable-badge">
              {actionable} actionable
            </Badge>
          )}
        </div>
        {error ? (
          <Card className="border-red-200 bg-red-50/40">
            <CardContent className="p-4 text-sm text-red-700">{error}</CardContent>
          </Card>
        ) : loading && !notifs ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="h-24 bg-slate-100 rounded-md animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(notifs?.notifications || []).map((item) => (
              <NotificationCard
                key={item.key}
                item={item}
                onClick={() => navigate(item.href)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Admin Tools */}
      <section data-testid="admin-tiles-section">
        <h2 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 font-body mb-3">Admin Tools</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map((tile) => (
            <TileCard key={tile.id} tile={tile} onClick={() => navigate(tile.href)} />
          ))}
        </div>
      </section>
    </div>
  );
}
